import { useState } from "react";
import { toast } from "sonner";
import PageHero from "@/components/site/PageHero";
import { Eyebrow, Reveal, GoldButton } from "@/components/site/motion";
import { IMAGES } from "@/data/content";
import { api } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";

const lookFor = [
  {
    num: "01",
    title: "A proven solution",
    text: "You've built something world-class and it works. We don't reinvent proven technology — we bring it home and deliver it locally.",
  },
  {
    num: "02",
    title: "South African ambition",
    text: "You want a trusted route into the South African market — with local manufacturing, technical delivery and commercial execution behind you.",
  },
  {
    num: "03",
    title: "Long-term thinking",
    text: "We build partnerships measured in years, not quarters — aligned incentives, shared growth and real skin in the game.",
  },
];

const areas = [
  "Cloud & Cybersecurity",
  "Payments Technology",
  "Media & Advertising",
  "Energy & Storage",
  "Electronics Manufacturing",
];

const inputDark =
  "w-full border-b border-white/25 bg-transparent py-3 text-sm text-white placeholder:text-smoke focus:border-gold focus:outline-none transition-colors duration-300";

export default function Partnerships() {
  useSEO(
    "Partnerships — Hapo Group",
    "We bring the world's best solutions to South Africa — we don't reinvent them. Partner with Hapo Group."
  );
  const [form, setForm] = useState({ name: "", email: "", organisation: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.sendEnquiry({
        name: form.name,
        email: form.email,
        company: "Partnership Enquiry",
        message: form.organisation ? `Organisation: ${form.organisation}\n\n${form.message}` : form.message,
      });
      setSent(true);
      toast.success("Enquiry sent — we'll be in touch.");
    } catch {
      toast.error("Could not send your enquiry — please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <PageHero
        eyebrow="Partnering for Growth"
        lines={["We bring the world's best to South Africa."]}
        description="We bring the world's best solutions to South Africa — we don't reinvent them."
        image={IMAGES.network}
      />

      <section className="bg-white px-6 py-24 lg:px-12 lg:py-[140px]" aria-label="Our approach to partnership">
        <div className="grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <Eyebrow>Our Approach to Partnership</Eyebrow>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-8">
            <p className="text-xl font-medium leading-relaxed tracking-tight text-ink md:text-2xl lg:text-3xl">
              Great technology doesn't need to be rebuilt to be brought home. Where a proven, world-class solution
              already exists, we partner with it — and add what only we can: South African manufacturing, technical
              delivery, and commercial execution. Our partners get a trusted route to market; our market gets the
              world's best, delivered locally.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-mist px-6 py-24 lg:px-12 lg:py-[140px]" aria-label="What we look for in a partner">
        <Eyebrow>What We Look For</Eyebrow>
        <h2 className="mt-5 max-w-2xl text-4xl font-extrabold tracking-tighter text-ink sm:text-5xl">
          The makings of a Hapo partnership.
        </h2>
        <div className="mt-16 grid gap-12 md:grid-cols-3" data-testid="partner-criteria">
          {lookFor.map((l, i) => (
            <Reveal key={l.num} delay={i * 0.1} data-testid={`partner-criterion-${l.num}`}>
              <p className="text-5xl font-extrabold tracking-tighter text-gold">{l.num}</p>
              <h3 className="mt-6 text-xl font-bold tracking-tight text-ink">{l.title}</h3>
              <p className="mt-4 text-sm leading-loose text-smoke">{l.text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-ink px-6 py-24 lg:px-12 lg:py-[140px]" aria-label="Partner with us" data-testid="partnership-enquiry-section">
        <div className="grid gap-16 lg:grid-cols-2">
          <Reveal>
            <Eyebrow>Partner With Us</Eyebrow>
            <h2 className="mt-5 text-4xl font-extrabold leading-tight tracking-tighter text-white sm:text-5xl">
              Have a proven solution looking for a South African partner?
            </h2>
            <p className="mt-8 max-w-lg text-sm leading-loose text-smoke md:text-base">
              We'd like to hear from you. We're actively looking for partners across:
            </p>
            <div className="mt-8 flex flex-wrap gap-3" data-testid="partnership-areas">
              {areas.map((a) => (
                <span
                  key={a}
                  className="rounded-full border border-white/20 px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.2em] text-white/80"
                >
                  {a}
                </span>
              ))}
            </div>
            <p className="mt-10 max-w-lg text-sm leading-loose text-smoke">
              Every enquiry lands directly with the group team and is answered personally.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            {sent ? (
              <div data-testid="partnership-success-message" className="border-l-2 border-gold bg-white/5 p-10">
                <h3 className="text-2xl font-bold tracking-tight text-white">
                  Thank you, {form.name.split(" ")[0]}.
                </h3>
                <p className="mt-4 text-sm leading-loose text-smoke">
                  Your partnership enquiry has been received. The right person from the group will get back to you
                  shortly.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-8" data-testid="partnership-form">
                <div className="grid gap-8 sm:grid-cols-2">
                  <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-smoke">Name *</span>
                    <input
                      required
                      value={form.name}
                      onChange={update("name")}
                      data-testid="partnership-name-input"
                      className={inputDark}
                      placeholder="Your full name"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-smoke">Email *</span>
                    <input
                      required
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      data-testid="partnership-email-input"
                      className={inputDark}
                      placeholder="you@company.com"
                    />
                  </label>
                </div>
                <label className="block">
                  <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-smoke">Organisation</span>
                  <input
                    value={form.organisation}
                    onChange={update("organisation")}
                    data-testid="partnership-organisation-input"
                    className={inputDark}
                    placeholder="Your company or organisation"
                  />
                </label>
                <label className="block">
                  <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-smoke">
                    Tell us about your solution *
                  </span>
                  <textarea
                    required
                    rows={5}
                    value={form.message}
                    onChange={update("message")}
                    data-testid="partnership-message-input"
                    className={`${inputDark} resize-none`}
                    placeholder="What do you offer, and what would a partnership with Hapo Group look like?"
                  />
                </label>
                <GoldButton type="submit" testId="partnership-submit-button">
                  {sending ? "Sending..." : "Start the Conversation"}
                </GoldButton>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </>
  );
}
